import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { getDashboardPathForRole, isTokenRevoked } from "../function";

interface AuthGuardProps {
  children: React.ReactNode;
}

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const currentPath = location.pathname;

    const logout = () => {
      localStorage.clear();
      setShouldRender(false);

      if (currentPath !== "/login") {
        navigate("/login", { replace: true });
      }
    };

    // No token
    if (!token) {
      if (currentPath === "/login") {
        setShouldRender(true);
      } else {
        logout();
      }
      setIsReady(true);
      return;
    }

    try {
      const decoded: DecodedToken = jwtDecode(token);
      const isExpired = decoded.exp * 1000 < Date.now();

      // Logout doesn't revoke the token server-side (see signOut's note) — this only catches
      // this exact token being pasted back into this browser's localStorage after a logout
      // that happened here. It's not real revocation; a different browser/device/client with
      // the same token is still honored by the backend until it naturally expires.
      if (isExpired || isTokenRevoked(token)) {
        logout();
      } else if (currentPath === "/login") {
        const userType = localStorage.getItem("user-type");
        navigate(getDashboardPathForRole(userType), { replace: true });
        setShouldRender(false);
      } else {
        setShouldRender(true);
      }
    } catch (error) {
      logout();
    } finally {
      setIsReady(true);
    }
  }, [location.pathname, navigate]);

  if (!isReady) return null;

  return shouldRender ? <>{children}</> : null;
};

export default AuthGuard;